import {
  Component,
  AfterViewInit,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewContainerRef
} from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { LMContextUtilService } from '@utils/lm-context-util.service';
import { LMReport, LMReportCategoriesByModule } from '../lm-reports';
import { ExpandableAreaComponent, SohoDataGridComponent, SohoPopupMenuComponent } from 'ids-enterprise-ng';
import { AppConstants, LMGlobalUtilService } from '@app/utils';
import * as urlTemplate from 'url-template';
import { LMReportsSearchComponent } from './lm-reports-search.component';
import {
  ConfirmationDialogResult,
  LMApplicationMenuSubMenuService,
  LMConfirmationMessageService, LMReportsCatalogService
} from '@app/components';
import { LMFormatUtilService, LMLoggerService, LMUrlUtilService } from '@app/services';
import { LMReportsCatalogToolbarService } from '@report/catalog/lm-reports-catalog-toolbar.service';

@Component({
  selector:    'lm-reports-list',
  templateUrl: 'lm-reports-list.component.html',
  styleUrls:   ['lm-reports-list.component.scss'],
  })

export class LMReportsListComponent implements AfterViewInit, OnInit, OnDestroy {
// Begin DataGrid column definitions
  baseRowTemplate = `
    <div class="datagrid-cell-layout lm-report-description">
      <div class="lm-report-description" style="margin-bottom: 8px"><i>{{description}}</i></div>
    </div>`;
  favoriteColumn: ListGridColumn = {
    id: 'isFavorite',
    name: $.createIcon({ icon: 'star-outlined', file: null }), //'',
    field: 'isFavorite',
    showEmpty: true,
    align: 'center',
    formatter: Soho.Formatters.Favorite,
    editor: Soho.Editors.Favorite,
    tooltip: this.favoriteTooltip,
    filterType: 'checkbox',
    width: 30, //30px
    // isEditable: this.favoriteIsClicked, - added programmatically in onInit
  };
  idColumn: ListGridColumn = {
    id: 'id',
    name: 'ID',
    field: 'id',
    hidden: true
  };
  titleColumn: ListGridColumn = {
    id: 'title',
    name: Soho.Locale.translate('CreateReportNameLabel'),
    field: 'title',
    cssClass: 'text-strong',
    formatter: this.reportNameFormatter,
    //href: this.reportNameHref, // Set programatically in onInit
    filterType: 'text'
  };
  categoryColumn: ListGridColumn = {
    id: 'category',
    name: Soho.Locale.translate('ReportCatalogSubcategory'),
    field: 'category',
    formatter: this.categoryFormatter,
    filterType: 'multiselect',
  };
  moduleColumn: ListGridColumn = {
    id: 'module',
    name: Soho.Locale.translate('ReportCatalogCategory'),
    field: 'module',
    formatter: Soho.Formatters.Dropdown,
    filterType: 'multiselect'
  };
  categoryUniqueColumn: ListGridColumn = {
    id: 'categoryUnique',
    name: 'UniqueCategoryForSearch',
    field: 'categoryUnique',
    formatter: Soho.Formatters.Dropdown,
    filterType: 'multiselect',
    hidden: true
  }
  busClassColumn: ListGridColumn = {
    id: 'busClass',
    name: Soho.Locale.translate('CreateReportBusinessClassLabel'),
    field: 'busClass',
    filterType: 'text'
  };
  sharedWithColumn: ListGridColumn = {
    id: 'sharedWith',
    name: Soho.Locale.translate('CreateReportSharing'),
    field: 'sharedWith',
    formatter: this.sharedWithFormatter,
    tooltip: this.sharedWithTooltip,
    filterType: 'text'
  };
  ownerColumn: ListGridColumn = {
    id: 'owner',
    name: Soho.Locale.translate('ReportOwner'),
    field: 'owner',
    formatter: this.ownerFormatter,
    filterType: 'text'
  };
  moreButtonColumn: ListGridColumn = {
    id: 'more',
    name: '',
    formatter: Soho.Formatters.Button,
    icon: 'more',
    align: 'center',
    cssClass: 'lm-report-more-button',
    showEmpty: false,
    width: 45, //45px
  };
  private listColumns: SohoDataGridColumn[] = [
    this.favoriteColumn,
    this.titleColumn,
    this.moreButtonColumn,
    this.moduleColumn,
    this.categoryColumn,
    this.busClassColumn,
    this.sharedWithColumn,
    this.ownerColumn,
    this.categoryUniqueColumn
  ];
  private saveUserSettings: SohoDataGridSaveUserSettings = {
    columns: false,
    rowHeight: false,
    sortOrder: false,
    pagesize: true,
    activePage: true,
    filter: false,
  }
  private selectedReport: LMReport;
  private emptyMessageNoData: SohoEmptyMessageOptions;
  private emptyMessageNoSearchResults: SohoEmptyMessageOptions;
  private pagesize = 20;

  public columnGridOptions: SohoDataGridOptions;
  public toolbarOptions: SohoToolbarOptions;

  public searchTags: Array<LMListSearchTag> = [];
  public displaySearch = false;
  private _contextMenuEvent: any;
  public displayContextMenu = false;

  private instanceId: string;
  private SAVED_SEARCHES_BUTTON_ID = 'CatalogSavedSearchMenuButton';
  private SAVED_SEARCHES_MORE_BUTTON_ID = 'CatalogMoreMenuButton';

  private _reportsArray: Array<LMReport>;
  @Input() set reportsArray(reportsArray: Array<LMReport>) {
    this._reportsArray = reportsArray;
    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.data = this._reportsArray;
      if (!this.displaySearch && reportsArray.length > 0) {
        this.displaySearch = true;
        setTimeout(() => {this.lmReportsSearchComponent.buildAndApplyFilter();});
      } else if (this.displaySearch && reportsArray.length === 0) {
        this.displaySearch = false;
        this.updateEmptyState();
      }
    }
  }
  get reportsArray(): Array<LMReport> {
    return this._reportsArray;
  }

  private _categoriesByModule: LMReportCategoriesByModule;
  @Input() set categoriesByModule(categoriesByModule: LMReportCategoriesByModule) {
    this._categoriesByModule = categoriesByModule;
    setTimeout(() => { // Need a tick for to prevent ExpressionChanged error
      this.displaySearch = this.reportsArray.length > 0;
    });
  }
  get categoriesByModule(): LMReportCategoriesByModule {
    return this._categoriesByModule;
  }
  @Input() categoriesByModuleOld: LMReportCategoriesByModule;
  @Input() reportListId: string;

  @Output() reportListEvent = new EventEmitter<any>();

  @ViewChild(ExpandableAreaComponent) expandableArea: ExpandableAreaComponent;
  @ViewChild(SohoDataGridComponent) sohoDataGridComponent: SohoDataGridComponent;
  @ViewChild(SohoPopupMenuComponent) sohoPopupMenu: SohoPopupMenuComponent;
  @ViewChild(LMReportsSearchComponent) lmReportsSearchComponent: LMReportsSearchComponent;
  @ViewChild('reportFormDialog', { read: ViewContainerRef, static: true }) reportPlaceholder: ViewContainerRef;


  constructor(
    public lmContextService: LMContextService,
    public lmGlobalUtilService: LMGlobalUtilService,
    public lmContextUtilService: LMContextUtilService,
    public lmUrlUtilService: LMUrlUtilService,
    public lmFormatUtilService: LMFormatUtilService,
    protected lmApplicationMenuSubMenuService: LMApplicationMenuSubMenuService,
    public lmConfirmationMessageService: LMConfirmationMessageService,
    private lmReportsCatalogToolbarService: LMReportsCatalogToolbarService,
    private lmReportsCatalogService: LMReportsCatalogService,
    private lmLoggerService: LMLoggerService,
  ) {}

  ngOnInit(){
    this.instanceId = this.lmLoggerService.instanceId;
    if (this.lmContextService.isMobileDevice()) {
      this.moreButtonColumn.cssClass = '';
    }
    this.titleColumn.href = ((row, cell, col, value) => {
      const actualValue: any = col;
      return actualValue.url ? actualValue.url : '#';
    });
    this.favoriteColumn.isEditable = this.favoriteIsClicked;

    this.columnGridOptions = <SohoDataGridOptions>{
      columns: this.listColumns,
      dataset: this._reportsArray,
      rowTemplate: this.baseRowTemplate,
      expandableRow: true,
      allowOneExpandedRow: false,
      reportListId: this.reportListId,
      selectable: 'single',
      paging: true,
      saveUserSettings: this.saveUserSettings, // Remember page size, current page, etc.
      // stickyHeader: true,
      pagesize: this.pagesize,
      pagesizes: [5, 10, 20, 50, 100, 200],
      rowReorder: false,
      editable: true,
      rowHeight: 'extra-small',
      isList: true,
      emptyMessage: null, //this.emptyMessageNoSearchResults,
      showDirty: false,
      enableTooltips: true,
      hidePagerOnOnePage: true,
      menuId: this.reportListId + '_context-menu',
    };
    this.initEmptyState(); // sets columnGridOptions
    if (this.reportsArray.length === 0) {
      this.columnGridOptions.emptyMessage = this.emptyMessageNoData;
    } else {
      this.columnGridOptions.emptyMessage = this.emptyMessageNoSearchResults;
    }

    // this.toolbarOptions = {
    //   title: ' ',
    //   results: false,
    //   dateFilter: false,
    //   actions: true,
    //   filterRow: true,
    // };
  }

  private initEmptyState() {
    this.emptyMessageNoSearchResults = {
      title: Soho.Locale.translate('ReportCatalogEmptySearchTitle'),
      icon: 'icon-empty-search-data-new',
      info: Soho.Locale.translate('ReportCatalogEmptySearchMessage'),
    };
    const emptyStateEmitter = this.reportListEvent;
    this.emptyMessageNoData = {
      title: Soho.Locale.translate('ReportCatalogEmptyCatalogTitle'),
      icon: 'icon-empty-no-data-new'
    };
    const context = this.lmContextService.getContext();
    if (context.configuredFeatures.includes(AppConstants.FEATURE_CreateReport)) {
      this.emptyMessageNoData.info = Soho.Locale.translate('ReportCatalogEmptyCatalogMessage'),
        this.emptyMessageNoData.button = {
          text: Soho.Locale.translate('CreateReport'),
          id: 'empty-state-btn-' + this.reportListId,
          isPrimary: true,
          click: (() => {
            emptyStateEmitter.emit({ type: 'emptyStateClicked', value: 'create-report' });
          })
        };
    }
  }

  public applyFilter(filterCriteriaArray: Array<SohoDataGridFilterCondition>) {
    this.sohoDataGridComponent.applyFilter(filterCriteriaArray);
  }

  public displaySearchTags($event) {
    if($event.tags) {
      this.searchTags = $event.tags;
    }
  }

  public doKeywordSearch($event) {
    const keyword = $event.keyword;
    (this.sohoDataGridComponent as any).datagrid.keywordSearch(keyword);
  }

  public get contextMenuEvent(): any {
    return this._contextMenuEvent;
  }

  ngOnDestroy = (): void => {
  };

  ngAfterViewInit(): void {
    setTimeout(() => {
      if(this.reportsArray.length > 0) {
        this.displaySearch = true;
        this.columnGridOptions.emptyMessage = this.emptyMessageNoSearchResults;
        this.pagesize = this.sohoDataGridComponent.pageSize();
        this.columnGridOptions.pagesize = this.pagesize;
        const sortColumn = this.lmReportsCatalogService.getReportsSortColumn();
        if (!sortColumn) {
          this.sohoDataGridComponent.setSortColumn('isFavorite', false);
        }
      }
    });
  }

  private updateEmptyState() {
    if (this.sohoDataGridComponent.emptyMessage) {
      if (this.reportsArray.length > 0) {
        this.sohoDataGridComponent.emptyMessage = this.emptyMessageNoSearchResults;
      } else {
        this.sohoDataGridComponent.emptyMessage = this.emptyMessageNoData;
      }
    }
  }

  private promptConfirmationForDelete(report?: LMReport ) {
    if (!report) {
      report = this.selectedReport;
    }
    const delStr = Soho.Locale.translate('Delete');
    const msg = {
      alertMessage: null,
      message: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ConfirmDelete'), delStr)
    };
    this.lmConfirmationMessageService.promptConfirmationMessage(msg)
      .subscribe(
        (result: any) => {
          if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
            this.reportListEvent.emit({ type: 'deleteReport', listId: this.reportListId, item: report });
          }
        }
      );
  }

  private viewReport(report: LMReport, urlParams?: LMUrlParams) {
    if (report.type.toUpperCase() === 'TASK') {
      this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(report.url.replace('<WebAppName>', LandmarkContext.webApp), report.id, this.reportPlaceholder);
    } else if (report.type.toUpperCase() === 'EPM') {
      if (this.lmContextService.getLogicalId().length > 0) { // Should never be false since EPM Reports are supposed to be filtered out in the backend
        infor.companyon.client.sendPrepareDrillbackMessage(report.url);
      } else {
        this.lmGlobalUtilService.displayToastMessage('Unable to Open Report', 'EPM Reports are only viewable in Portal');
      }
    } else {
      this.lmContextService.setClearNextPageState(true, false);
      let navUrl = report.url.replace('<WebAppName>', LandmarkContext.webApp);
      if (urlParams) {
        navUrl = this.lmUrlUtilService.updateUrlParams(navUrl, urlParams);
      }
      this.lmGlobalUtilService.navigateByUrl(navUrl);
    }
  }

  /* ======================= *
   * SohoDatagrid Functions *
   * ======================= */
  onRowClicked(event?: SohoDataGridRowClicked) {
    if (!event || isNaN(event.row) || !event.item.url) {
      return;
    }
    event.originalEvent.preventDefault();
    const eventTarget = event.originalEvent.target as HTMLElement;
    if (eventTarget.classList.contains('hyperlink') || eventTarget.parentElement?.classList.contains('hyperlink')) {
      this.viewReport(event.item);
    } else if (eventTarget.classList.contains('print-icon') || eventTarget.parentElement?.classList.contains('print-icon')) {
      const compiledUrlTemplate = urlTemplate.parse(this.lmUrlUtilService.getBaseUrl() + '/navigation/{resourceId}.{navigationName}');
      const resourceId = 'UserFolder(' + event.item.id + ')';
      const url = compiledUrlTemplate.expand({ resourceId, navigationName: 'MyUserReports' });
      this.lmGlobalUtilService.navigateByUrl(url);
    } else if (eventTarget.classList.contains('lm-report-more-button') || eventTarget.parentElement?.classList.contains('lm-report-more-button')) {
      this.onContextMenu(event);
    }
    this.sohoDataGridComponent.clearDirty();
  }

  onColumnSort(event?: SohoDataGridSortedEvent) {
    this.lmReportsCatalogService.setReportsSortColumn(event.sortId, event.sortAsc);
  }
  // -- BEGIN CONTEXT MENU FUNCTIONS --
  onBeforeContextMenuOpen = (response: SohoPopupMenuResponseFunction, options: any) => {
    // console.log('onBeforeContextMenuOpen');
    if (options.isSubmenu) {
      if (options.contextElement[0]?.id === this.SAVED_SEARCHES_BUTTON_ID) {
        this.lmReportsCatalogToolbarService.buildSavedSearchContextMenu(this.selectedReport, response, false);
      } else if (options.contextElement[0]?.id === this.SAVED_SEARCHES_MORE_BUTTON_ID) {
        this.lmReportsCatalogToolbarService.buildSavedSearchContextMenu(this.selectedReport, response, true);
      }
    } else {
      this.lmReportsCatalogToolbarService.buildReportContextMenu(this.selectedReport, response);
    }
  }
  onContextMenu(event: SohoDataGridRowClicked): void {
    this.lmLoggerService.log([ this, this.instanceId, 'onContextMenu()', event ]);
    // -------------------------------------------------------------
    // Destroy any existing popup menu so that the DOM markup is completely
    // removed. This is needed before the new menu is built and displayed.
    // -------------------------------------------------------------
    if (this.sohoPopupMenu) {
      this.sohoPopupMenu.close();
      if (this.sohoPopupMenu) {
        this.sohoPopupMenu.destroy();
      }
    }
    // Select triggering row and save into variable
    this.sohoDataGridComponent.selectRows(event.row);
    const report = event.item;
    if (report) {
      this.selectedReport = event.item;
    } else {
      this.selectedReport = null;
      return; // do not display context menu if not tied to a report
    }
    // display the browser's context menu when the target is an anchor element with an href specified
    if (event.originalEvent.target && event.originalEvent.target.href) {
      return;
    }
    // -------------------------------------------------------------
    // cause the new right click menu markup to be built, which
    // causes the popup menu to be displayed.
    // -------------------------------------------------------------
    setTimeout(() => {
      event.originalEvent.preventDefault();
      this._contextMenuEvent = event.originalEvent;
      this.displayContextMenu = true;
    });
  }
  onContextMenuItemSelected(menuItemEvent: SohoPopupMenuEvent) {
    const actionName = menuItemEvent.args[0]?.getAttribute('data-action-name');
    if (actionName) {
      if (actionName === 'delete') {
        this.promptConfirmationForDelete(this.selectedReport);
      } else if (actionName === 'open') {
        const userListView = menuItemEvent.args[0]?.getAttribute('data-user-list-view');
        if (userListView) {
          this.viewReport(this.selectedReport, {userListView});
        } else {
          this.viewReport(this.selectedReport);
        }
      } else {
        this.reportListEvent.emit({ type: actionName, item: this.selectedReport, listId: this.reportListId });
      }
    }
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  onCloseContextMenu(event: any) {
    this._contextMenuEvent = null;
    this.selectedReport = null;
    this.displayContextMenu = false;
  }
  // -- END CONTEXT MENU FUNCTIONS --

  onDatagridKeydown(event: any) {
    if (event.key && event.key === 'Enter') {
      event.target.click();
    }
  }

  onPageSizeChange(info: SohoPagerPagingInfo) {
    if (info.pagesize) {
      this.pagesize = info.pagesize;
      this.columnGridOptions.pagesize = this.pagesize;
    }
  }

  /* ================================= *
   * SohoDatagrid Formatters & Editors *
   * ================================= */
  public favoriteIsClicked = (row, cell, value, col, item, api) => {
    if (api && item) { // Prevents favorites from being updated while datagrid is rendering
      this.reportListEvent.emit({ type: 'favoriteClicked', value, item, listId: this.reportListId });
    }
    return true;
  }

  public favoriteTooltip(row, cell, value, col, item, api) {
    if (!value) {
      return Soho.Locale.translate('ReportCatalogFavorite');
    } else {
      return Soho.Locale.translate('ReportCatalogRemoveFavorite');
    }
  }

  public favoriteAlwaysFilledStarFormatter(row, cell, value, col, item, api) {
    return '<span aria-label="Favorite" class="icon-favorite">' + $.createIcon({ icon: 'star-filled', file: null }) + '</span>';
  }

  public reportNameFormatter(row, cell, value, col, item, api) {
    value = value.trim();
    value = value === '' ? item.name : value;
    const origUrl = item.url;
    item.url = item.url.replace('<WebAppName>', LandmarkContext.webApp);
    let link = Soho.Formatters.Hyperlink(row, cell, value, col, item, api);
    item.url = origUrl; // Need to set it back to the version with <WebAppName> after formatting the hyperlink
    if(item.hasUserReportRecords) {
      link += '<button type="button" class="btn-icon row-btn print-icon" style="margin-left: 3px;">' + $.createIcon({ icon: 'print', file: null }) + '</button>';
    }
    return link;
  }

  public categoryFormatter(row, cell, value, col, item, api) {
    if (col.id === 'category' && value === ' General') {
      value = Soho.Locale.translate('General');
    }
    return Soho.Formatters.Dropdown(row, cell, value, col, item, api);
  }

  public ownerFormatter(row, cell, value, col, item, api) {
    const text = Soho.Formatters.Text(row, cell, value, col, item, api);
    if (text === LandmarkContext.actorId) {
      return `<span class="tag lpl-purple-bg" style="color: #fff;">${text}</span>` ;
    }
    return text;
  }

  public sharedWithFormatter(row, cell, value, col, item, api) {
    let text = Soho.Formatters.Text(row, cell, value, col, item, api);
    if (text !== '') {
      let iconName = 'group-selection';
      if (item.sharedWithType === 'Private') {
        iconName = 'locked';
        text = Soho.Locale.translate('CreateReportPrivate');
      } else if (value === 'Public') {
        iconName = 'url';
      }
      return $.createIcon({ icon: iconName, file: null }) + `<span style="margin-left: 3px;">${text}</span>`;
    }
    return '';
  }

  public sharedWithTooltip(row, cell, value, col, item, api) {
    let text = Soho.Formatters.Text(row, cell, value, col, item, api);
    if (text !== '') {
      let iconName = 'group-selection';
      if (item.sharedWithType === 'Private') {
        iconName ='locked';
        text = Soho.Locale.translate('CreateReportPrivate');
      } else if (value === 'Public'){
        iconName = 'url';
      }
      if (item.sharedWithType.length > 0 && item.sharedWithType !== 'Private' && item.sharedWithType !== 'Public') {
        text = item.sharedWithType + ': ' + text;
      }
      return $.createIcon({ icon: iconName, file: null }) + `<span style="margin-left: 3px;">${text}</span>`;
    }
    return null;
  }
}
